import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWishlist } from '@/contexts/WishlistContext';
import api from '@/lib/api';
import { Heart, Loader2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Wishlist = () => {
  const { user } = useAuth();
  const { toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWishlist();
  }, [user]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wishlist');
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    await toggleWishlist(productId);
    setProducts(prev => prev.filter(p => p.id !== productId));
    toast.success('Removed from wishlist');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="wishlist-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="wishlist-title">
        My Wishlist
      </h1>
      <p className="text-lg text-muted-foreground font-light mb-12">
        {products.length} {products.length === 1 ? 'item' : 'items'} saved
      </p>

      {products.length === 0 ? (
        <div className="text-center py-16" data-testid="wishlist-empty">
          <Heart className="h-16 w-16 mx-auto text-muted-foreground/30 mb-6" />
          <h2 className="text-2xl font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your wishlist is empty
          </h2>
          <p className="text-muted-foreground mb-8">Save your favourite Bandhani pieces here</p>
          <Link to="/products">
            <Button className="rounded-full px-8 py-6 uppercase tracking-widest text-sm" data-testid="wishlist-shop-btn">
              <ShoppingBag className="mr-2 h-4 w-4" /> Browse Collection
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8" data-testid="wishlist-grid">
          {products.map((product) => (
            <div key={product.id} className="group relative" data-testid={`wishlist-item-${product.id}`}>
              <Link to={`/products/${product.id}`}>
                <div className="relative overflow-hidden mb-4 h-[400px] rounded-xl">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-xl"
                  />
                </div>
                <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {product.name}
                </h3>
                <p className="text-lg font-light">₹{product.price?.toLocaleString('en-IN')}</p>
              </Link>
              <button
                onClick={() => handleRemove(product.id)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-all"
                data-testid={`remove-wishlist-${product.id}`}
              >
                <Heart className="h-5 w-5 fill-red-500 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
